import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import {Button} from '../button/button';
import {ButtonMover} from '../button-mover/button-mover';
import {colors} from '../colors/colors';
import type {ColorName} from '../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {ExternalLinkIcon} from '../icon/svg/external-link-icon';
import {GlossaryIcon} from '../icon/svg/glossary-icon';
// @ts-expect-error - imported from .js/.jsx file
import {PlayButtonIcon} from '../icon/svg/play-button-icon';
import {Link} from '../link/link';

import type {CueCardCta, CueCardVariant, GradientConfig} from './cue-card';

import styles from './cue-card.module.css';

export function getCta(
    cta?: CueCardCta | null,
    variant?: CueCardVariant
): React.ReactElement | null {
    if (cta) {
        let ctaIcon;
        if (cta.actionType === 'external-link') {
            ctaIcon = <ExternalLinkIcon size={13} wrapWithDiv={false} />;
        } else if (cta.actionType === 'modal') {
            ctaIcon = <GlossaryIcon size={13} wrapWithDiv={false} />;
        } else if (cta.actionType === 'video') {
            ctaIcon = <PlayButtonIcon size={13} wrapWithDiv={false} />;
        }

        const ctaText = cta.text ? (
            <div className={styles['cta-text']}>
                {ctaIcon}
                <div>{cta.text}</div>
            </div>
        ) : undefined;

        if (cta.url && ctaText) {
            const {newTab = true} = cta;

            let as, to, href;
            if (cta.actionType === 'routed-link') {
                as = RoutedLink;
                to = cta.url;
            } else {
                href = cta.url;
            }

            return (
                <Link
                    // @ts-expect-error
                    as={as}
                    to={to}
                    href={href}
                    newTab={newTab}
                    onClick={(e?: React.SyntheticEvent<any> | null) => {
                        if (cta.onClick) {
                            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                            e && e.stopPropagation();
                            cta.onClick();
                        }
                    }}
                    variant={variant === 'ai' ? 'ai' : 'primary'}
                    size='small'
                >
                    {ctaText}
                </Link>
            );
        } else if (cta.onClick && ctaText) {
            return (
                <ButtonMover size='small' direction='left'>
                    <Button
                        as='div'
                        onClick={(e: React.SyntheticEvent) => {
                            e.stopPropagation();
                            cta.onClick?.();
                        }}
                        variant={variant === 'ai' ? 'text-ai' : 'text-primary'}
                        size='small'
                        fitContent={true}
                    >
                        {ctaText}
                    </Button>
                </ButtonMover>
            );
        }
    }

    return null;
}

export function getDescription(
    description: string | null | undefined | React.ComponentType<any>
): React.ReactElement | null {
    if (!description) return null;

    if (typeof description === 'string') {
        // @ts-expect-error
        return description;
    } else {
        const Description = description;

        return <Description />;
    }
}

export function onCardClick(cueCardCta: CueCardCta, history: RouterHistory) {
    if (cueCardCta.url) {
        if (cueCardCta.actionType === 'routed-link') {
            history.push(cueCardCta.url);
        } else if (cueCardCta.actionType === 'external-link') {
            window.open(cueCardCta.url, '_blank');
        }
    }

    if (cueCardCta.onClick) {
        cueCardCta.onClick();
    }
}

function getIllustrationUrl(path: string): string {
    // If the path is already a full URL or starts with /, use it directly
    if (path.startsWith('/') || path.startsWith('http')) {
        return path;
    }

    return `/include/illustrations/${path}.svg`;
}

export function getThumbnail(
    thumbnailIllustration: string | null | undefined | React.ReactNode,
    hasPlayButton?: boolean | null,
    ctaOnClick?: (() => void) | null
): React.ReactElement {
    return (
        <>
            {typeof thumbnailIllustration === 'string' ? (
                <>
                    <img src={getIllustrationUrl(thumbnailIllustration)} alt='Info graphic' />
                    {hasPlayButton ? (
                        // @ts-expect-error
                        <div onClick={ctaOnClick} className={styles['play-button']}>
                            <PlayButtonIcon size={50} wrapWithDiv={false} color={colors.white} />
                        </div>
                    ) : undefined}
                </>
            ) : (
                thumbnailIllustration
            )}
        </>
    );
}

export function buildGradientString(config: GradientConfig): string {
    const {colors: gradientColors, angle = 90} = config;

    const colorStops = gradientColors.map((colorName, index) => {
        const hexColor = colors[colorName];
        const stop = Math.round((index / (gradientColors.length - 1)) * 100);

        return `${hexColor} ${stop}%`;
    });

    return `linear-gradient(${angle}deg, ${colorStops.join(', ')})`;
}

export function getStyleVariables(
    thumbnailBackgroundColor?: ColorName,
    thumbnailBackgroundColorHover?: ColorName,
    thumbnailBackgroundGradientHover?: GradientConfig
): any | null | undefined {
    const styleVars: Record<string, any> = {};

    if (thumbnailBackgroundColor) {
        styleVars['--thumbnail-background-color'] = colors[thumbnailBackgroundColor];
        styleVars['--thumbnail-background-color-hover'] = thumbnailBackgroundColorHover
            ? colors[thumbnailBackgroundColorHover]
            : colors[thumbnailBackgroundColor];
    }

    if (thumbnailBackgroundGradientHover) {
        styleVars['--thumbnail-background-gradient-hover'] = buildGradientString(
            thumbnailBackgroundGradientHover
        );
    }

    return Object.keys(styleVars).length > 0 ? styleVars : undefined;
}

export function getIconColorFromVariant(variant: CueCardVariant): string {
    switch (variant) {
        case 'ai':
            return colors.aiPurplePrimary;
        case 'feature':
            return colors.blue;
        default:
            return colors.greyDk;
    }
}
